import React, { useState } from "react";

const emptyRow = { itemId: "", description: "", vessel: "" };

const inlineInput =
  "w-full rounded border border-zinc-300 bg-white px-2 py-1 text-sm focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600/20";

const ToolTable = ({ tools, dprId, onCreate, onUpdate, onDelete }) => {
  const [newRow, setNewRow] = useState(emptyRow);
  const [editingId, setEditingId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const handleAdd = () => {
    if (!newRow.itemId.trim() || !newRow.description.trim()) return;
    onCreate({
      dprId,
      itemId: newRow.itemId.trim(),
      description: newRow.description.trim(),
      vessel: newRow.vessel.trim(),
      sortOrder: tools.length,
    });
    setNewRow(emptyRow);
  };

  const handleStartEdit = (id, field, value) => {
    setEditingId(id);
    setEditField(field);
    setDraftValue(value ?? "");
  };

  const handleCommit = () => {
    if (!editingId || !editField) return;
    const entry = tools.find((t) => t.id === editingId);
    if (entry) {
      onUpdate(editingId, { ...entry, [editField]: draftValue });
    }
    setEditingId(null);
    setEditField(null);
    setDraftValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCommit();
    if (e.key === "Escape") {
      setEditingId(null);
      setEditField(null);
    }
  };

  const renderCell = (row, field, mono = false) => {
    const isEditing = editingId === row.id && editField === field;
    if (isEditing) {
      return (
        <input
          type="text"
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          className={`w-full rounded border border-navy-600 bg-white px-1.5 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-600/20 ${
            mono ? "font-mono tabular-nums" : ""
          }`}
        />
      );
    }
    return (
      <div
        onClick={() => handleStartEdit(row.id, field, row[field])}
        className={`min-h-[1.5em] cursor-pointer rounded px-1 -mx-1 hover:bg-navy-50/60 ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {row[field] || <span className="text-zinc-300">—</span>}
      </div>
    );
  };

  return (
    <section>
      <h3 className="section-eyebrow mb-3">Tool / equipment usage</h3>
      <table className="table-hairline">
        <thead>
          <tr>
            <th className="w-32">Item ID</th>
            <th>Description</th>
            <th className="w-40">Vessel</th>
            <th className="w-16 text-right" />
          </tr>
        </thead>
        <tbody>
          {tools.map((row) => (
            <tr key={row.id}>
              <td>{renderCell(row, "itemId", true)}</td>
              <td>{renderCell(row, "description")}</td>
              <td>{renderCell(row, "vessel")}</td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-xs font-medium text-zinc-400 hover:text-rust-600"
                  onClick={() => onDelete(row.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          <tr>
            {["itemId", "description", "vessel"].map((field) => (
              <td key={field}>
                <input
                  type="text"
                  value={newRow[field]}
                  onChange={(e) =>
                    setNewRow((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  placeholder={
                    field === "itemId"
                      ? "Item ID"
                      : field === "description"
                      ? "Description"
                      : "Vessel"
                  }
                  className={`${inlineInput} ${field === "itemId" ? "font-mono tabular-nums" : ""}`}
                />
              </td>
            ))}
            <td className="text-right">
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newRow.itemId.trim() || !newRow.description.trim()}
                className="text-xs font-medium text-navy-600 hover:text-navy-700 disabled:opacity-40"
              >
                Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
};

export default ToolTable;
